import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import SalesCard from "../../../Components/Dashboard/SalesCard";
import LeadsCard from "../../../Components/Dashboard/LeadsCard";
import { db } from "../../../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  getCountFromServer,
  doc,
  getDoc,
} from "firebase/firestore";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { formatToday } from "../../../utils/date";

interface SalesStat {
  id: string;
  name: string;
  todayContacted: number;
  todayPotential: number;
  todayEnrolled: number;
  todayLost: number;
  totalContacted: number;
  totalPotential: number;
  totalEnrolled: number;
  totalLost: number;
}

const DashboardHome = () => {
  const [salesStats, setSalesStats] = useState<SalesStat[]>([]);
  const { userRole, userBranch, currentUserUid } = useAuthUser();
  const [newLeadsCount, setNewLeadsCount] = useState<number>(0);
  const [potentialLeadsCount, setPotentialLeadsCount] = useState<number>(0);

  // Personal new and potential leads count
  useEffect(() => {
    const fetchNewLeads = async () => {
      if (!currentUserUid) return;
      try {
        const leadsRef = collection(db, "leads");
        const qNewLeads = query(
          leadsRef,
          where("assigneeId", "==", currentUserUid),
          where("status", "==", "New"),
        );
        const snapshotNewLeads = await getCountFromServer(qNewLeads);
        setNewLeadsCount(snapshotNewLeads.data().count);

        const qPotentialLeads = query(
          leadsRef,
          where("assigneeId", "==", currentUserUid),
          where("status", "==", "Potential"),
        );
        const snapshotPotentialLeads =
          await getCountFromServer(qPotentialLeads);
        setPotentialLeadsCount(snapshotPotentialLeads.data().count);
      } catch (error) {
        console.error("Error fetching new leads count:", error);
      }
    };

    if (currentUserUid) {
      fetchNewLeads();
    }
  }, [currentUserUid]);

  // Sales stats
  useEffect(() => {
    const fetchSalesStats = async () => {
      if (userRole !== "super admin" && userRole !== "Branch Manager") return;

      try {
        const usersRef = collection(db, "users");
        let qUsers;

        if (userRole === "Branch Manager") {
          if (!userBranch) return;
          qUsers = query(
            usersRef,
            where("role", "in", ["sales", "part-time sales"]),
            where("branch", "==", userBranch),
          );
        } else {
          // Super admin gets everyone
          qUsers = query(
            usersRef,
            where("role", "in", ["Branch Manager", "sales", "part-time sales"]),
          );
        }

        const userSnaps = await getDocs(qUsers);

        const users = userSnaps.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().displayName || "Unknown",
        }));

        const todayStr = formatToday();

        const statsPromises = users.map(async (user) => {
          const leadsRef = collection(db, "leads");

          // Total Contacted
          const qTotalContacted = query(
            leadsRef,
            where("assigneeId", "==", user.id),
            where("status", "==", "Contacted"),
          );

          // Total Potential
          const qTotalPotential = query(
            leadsRef,
            where("assigneeId", "==", user.id),
            where("status", "==", "Potential"),
          );

          // Total Enrolled
          const qTotalEnrolled = query(
            leadsRef,
            where("assigneeId", "==", user.id),
            where("status", "==", "Enrolled"),
          );

          // Total Lost
          const qTotalLost = query(
            leadsRef,
            where("assigneeId", "==", user.id),
            where("status", "==", "Lost"),
          );

          // Today Contacted
          const qTodayContacted = query(
            leadsRef,
            where("assigneeId", "==", user.id),
            where("status", "==", "Contacted"),
            where("lastTouchedDate", "==", todayStr),
          );

          // Today Potential
          const qTodayPotential = query(
            leadsRef,
            where("assigneeId", "==", user.id),
            where("status", "==", "Potential"),
            where("lastTouchedDate", "==", todayStr),
          );

          // Today Enrolled
          const qTodayEnrolled = query(
            leadsRef,
            where("assigneeId", "==", user.id),
            where("status", "==", "Enrolled"),
            where("lastTouchedDate", "==", todayStr),
          );

          // Today Lost
          const qTodayLost = query(
            leadsRef,
            where("assigneeId", "==", user.id),
            where("status", "==", "Lost"),
            where("lastTouchedDate", "==", todayStr),
          );

          const [
            totalContactedSnap,
            totalPotentialSnap,
            totalEnrolledSnap,
            totalLostSnap,
            todayContactedSnap,
            todayPotentialSnap,
            todayEnrolledSnap,
            todayLostSnap,
          ] = await Promise.all([
            getCountFromServer(qTotalContacted),
            getCountFromServer(qTotalPotential),
            getCountFromServer(qTotalEnrolled),
            getCountFromServer(qTotalLost),
            getCountFromServer(qTodayContacted),
            getCountFromServer(qTodayPotential),
            getCountFromServer(qTodayEnrolled),
            getCountFromServer(qTodayLost),
          ]);

          return {
            id: user.id,
            name: user.name,
            totalContacted: totalContactedSnap.data().count,
            totalPotential: totalPotentialSnap.data().count,
            totalEnrolled: totalEnrolledSnap.data().count,
            totalLost: totalLostSnap.data().count,
            todayContacted: todayContactedSnap.data().count,
            todayPotential: todayPotentialSnap.data().count,
            todayEnrolled: todayEnrolledSnap.data().count,
            todayLost: todayLostSnap.data().count,
          };
        });

        const stats = await Promise.all(statsPromises);
        setSalesStats(stats);
      } catch (error) {
        console.error("Error fetching sales stats:", error);
      }
    };

    if (userRole) {
      fetchSalesStats();
    }
  }, [userRole, userBranch]);

  const assigneeFilter =
    (userRole === "Branch Manager" || userRole === "super admin") &&
    currentUserUid
      ? currentUserUid
      : undefined;

  return (
    <div className="relative flex flex-col gap-6 pb-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Dashboard</h2>
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-sm font-semibold text-gray-500">Personal Stats</p>
        <div className="grid grid-cols-4 gap-4">
          <LeadsCard
            leadsCount={newLeadsCount}
            title="New Leads"
            color="teal"
            filterStatus="New"
            assigneeId={assigneeFilter}
          />
          <LeadsCard
            leadsCount={potentialLeadsCount}
            title="Potential Leads"
            color="green"
            icon={Sparkles}
            filterStatus="Potential"
            assigneeId={assigneeFilter}
          />
        </div>
      </div>

      {(userRole === "super admin" || userRole === "Branch Manager") && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-gray-500">Sales Stats</p>

          <div className="grid grid-cols-4 gap-4">
            {salesStats.length > 0 ? (
              salesStats.map((stat) => (
                <SalesCard
                  key={stat.id}
                  assigneeId={stat.id}
                  name={stat.name}
                  todayContacted={stat.todayContacted}
                  todayPotential={stat.todayPotential}
                  todayEnrolled={stat.todayEnrolled}
                  todayLost={stat.todayLost}
                  totalContacted={stat.totalContacted}
                  totalPotential={stat.totalPotential}
                  totalEnrolled={stat.totalEnrolled}
                  totalLost={stat.totalLost}
                />
              ))
            ) : (
              <p className="col-span-4 text-gray-500">
                Loading dashboard data...
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardHome;
